import { createContext, useContext, useState, useCallback } from "react";
import { trpc } from "@/lib/trpc";

export type MissionPhase = "welcome" | "audience" | "design" | "rules" | "marketing" | "launch";

export interface GameConfig {
  title: string;
  description: string;
  gameType: string;
  targetAudience: string;
  influencerNiche: string;
  entryFee: number;
  maxPlayers: number;
  duration: string;
  rules: string[];
  winCondition: string;
  submissionFormat: string;
  submissionType: "image" | "text" | "video" | "link" | "numeric";
  votingMethod: "community_vote" | "judge_panel" | "auto_score" | "hybrid";
  influencerChannelUrl: string;
  playerShareTemplate: string;
  influencerFeatureTemplate: string;
  hostCut: number;
  promoterCut: number;
  viralScore: number;
  complianceScore: number;
  marketingHook: string;
  socialCaption: string;
  emailPitch: string;
  socialDistributionMode: "direct" | "hybrid" | "api";
  socialHashtag: string;
  socialEngagementWeight: number;
}

export interface ChatMessage {
  id: string;
  role: "assistant" | "user";
  content: string;
  timestamp: Date;
  phase?: MissionPhase;
}

interface GameCreatorState {
  phase: MissionPhase;
  messages: ChatMessage[];
  gameConfig: GameConfig;
  isTyping: boolean;
  phaseProgress: Record<MissionPhase, number>;
}

interface GameCreatorContextType extends GameCreatorState {
  sendMessage: (content: string) => void;
  setPhase: (phase: MissionPhase) => void;
  updateGameConfig: (updates: Partial<GameConfig>) => void;
  resetCreator: () => void;
}

const defaultGameConfig: GameConfig = {
  title: "",
  description: "",
  gameType: "",
  targetAudience: "",
  influencerNiche: "",
  entryFee: 0, // ticket price in dollars ($1–$10,000); the AI recommends one, 0 only if the creator asks for a free event
  maxPlayers: 100,
  duration: "1 hour",
  rules: [],
  winCondition: "",
  submissionFormat: "",
  submissionType: "numeric",
  votingMethod: "auto_score", // objective scoring (time, score, count)
  influencerChannelUrl: "",
  playerShareTemplate: "",
  influencerFeatureTemplate: "",
  hostCut: 10,
  promoterCut: 10,
  viralScore: 0,
  complianceScore: 0,
  marketingHook: "",
  socialCaption: "",
  emailPitch: "",
  socialDistributionMode: "direct",
  socialHashtag: "",
  socialEngagementWeight: 50,
};

const WELCOME_MESSAGE = `Welcome to the **GamePoint AI Game Creator**! I'm your strategic game design advisor.

I'll guide you through creating a viral, skill-based competition that can be monetized on the GamePoint platform. We'll work through 5 phases:

1. **Audience Analysis** — Understand who will play
2. **Game Design** — Create the perfect challenge
3. **Rule Validation** — Ensure compliance and clarity
4. **Marketing Materials** — Generate promotional content
5. **Launch Configuration** — Finalize and prepare to go live

**Let's start with the most important question: Who is this game for?**

You can:
- **Paste a URL** to a creator's channel, website, or social profile and I'll analyze their audience
- Tell me about a specific influencer or content creator
- Describe a general audience (gamers, fitness enthusiasts, etc.)
- Share a trending event or topic you want to build a game around`;

const phaseOrder: MissionPhase[] = ["welcome", "audience", "design", "rules", "marketing", "launch"];

const GameCreatorContext = createContext<GameCreatorContextType | undefined>(undefined);

// Detect URLs in text
function extractUrls(text: string): string[] {
  const urlRegex = /https?:\/\/[^\s<>"{}|\\^`\[\]]+/gi;
  return text.match(urlRegex) || [];
}

// Check if a phase's required fields are satisfied before allowing advancement
function isPhaseComplete(phase: MissionPhase, config: GameConfig): boolean {
  switch (phase) {
    case "welcome":
      return true; // Can always leave welcome
    case "audience":
      // Need at least a target audience identified
      return !!config.targetAudience;
    case "design":
      // Need a game title and type chosen
      return !!config.title && !!config.gameType;
    case "rules":
      // Need win condition and at least one rule
      return !!config.winCondition && config.rules.length > 0;
    case "marketing":
      // Need at least one marketing material generated
      return !!config.socialCaption || !!config.emailPitch || !!config.marketingHook;
    case "launch":
      return true;
    default:
      return false;
  }
}

// Infer phase from LLM response content, with gating based on config completeness
function inferPhase(content: string, currentPhase: MissionPhase, config: GameConfig): MissionPhase {
  const lower = content.toLowerCase();

  // Determine what the LLM thinks the phase should be
  let suggestedPhase = currentPhase;

  if (lower.includes("launch summary") || lower.includes("ready to launch") || lower.includes("game configuration is complete") || lower.includes("launch strategy") || lower.includes("launch confirmation") || lower.includes("finalize and launch") || lower.includes("configured your")) {
    suggestedPhase = "launch";
  } else if ((lower.includes("marketing material") || lower.includes("social media caption") || lower.includes("influencer pitch") || lower.includes("promotional content")) && (lower.includes("social caption") || lower.includes("email pitch") || lower.includes("marketing hook"))) {
    suggestedPhase = "marketing";
  } else if ((lower.includes("compliance") || lower.includes("rule validation") || lower.includes("win condition") || lower.includes("rules:")) && (currentPhase === "design" || currentPhase === "rules")) {
    suggestedPhase = "rules";
  } else if ((lower.includes("game idea") || lower.includes("game suggestion") || (lower.includes("here are") && (lower.includes("challenge") || lower.includes("game")))) && (currentPhase === "audience" || currentPhase === "design")) {
    suggestedPhase = "design";
  } else if ((lower.includes("audience") || lower.includes("analyzing") || lower.includes("content creator") || lower.includes("their audience")) && (currentPhase === "welcome" || currentPhase === "audience")) {
    suggestedPhase = "audience";
  }

  // Gate: only advance if the current phase's required fields are satisfied
  const suggestedIdx = phaseOrder.indexOf(suggestedPhase);
  const currentIdx = phaseOrder.indexOf(currentPhase);

  if (suggestedIdx > currentIdx) {
    // Special case: when jumping to launch, marketing is optional (user can skip it)
    // Only require the core game design phases (audience, design, rules) to be complete
    if (suggestedPhase === "launch") {
      const corePhases: MissionPhase[] = ["welcome", "audience", "design", "rules"];
      for (const cp of corePhases) {
        const cpIdx = phaseOrder.indexOf(cp);
        if (cpIdx >= currentIdx && cpIdx < suggestedIdx && !isPhaseComplete(cp, config)) {
          return currentPhase; // Block — a core phase is not complete
        }
      }
    } else {
      // Check all intermediate phases are complete
      for (let i = currentIdx; i < suggestedIdx; i++) {
        if (!isPhaseComplete(phaseOrder[i], config)) {
          return currentPhase; // Block advancement — current phase not complete
        }
      }
    }
  }

  return suggestedPhase;
}

// Calculate phase progress based on current phase
function calculateProgress(phase: MissionPhase): Record<MissionPhase, number> {
  const progress: Record<MissionPhase, number> = {
    welcome: 100,
    audience: 0,
    design: 0,
    rules: 0,
    marketing: 0,
    launch: 0,
  };

  const idx = phaseOrder.indexOf(phase);
  for (let i = 1; i < phaseOrder.length; i++) {
    if (i < idx) {
      progress[phaseOrder[i]] = 100;
    } else if (i === idx) {
      progress[phaseOrder[i]] = 50;
    }
  }

  return progress;
}

export function GameCreatorProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<GameCreatorState>({
    phase: "welcome",
    messages: [
      {
        id: "welcome-1",
        role: "assistant",
        content: WELCOME_MESSAGE,
        timestamp: new Date(),
        phase: "welcome",
      },
    ],
    gameConfig: defaultGameConfig,
    isTyping: false,
    phaseProgress: {
      welcome: 100,
      audience: 0,
      design: 0,
      rules: 0,
      marketing: 0,
      launch: 0,
    },
  });

  const chatMutation = trpc.gameAdvisor.chat.useMutation();
  // During the beta every event is scored objectively (no vote- or likes-decided winners).
  const betaStatus = trpc.coupon.betaStatus.useQuery(undefined, { staleTime: 60_000 });
  const betaActive = betaStatus.data?.active === true;
  const analyzeUrlMutation = trpc.gameAdvisor.analyzeUrl.useMutation();

  const sendMessage = useCallback(
    async (content: string) => {
      const userMsg: ChatMessage = {
        id: `user-${Date.now()}`,
        role: "user",
        content,
        timestamp: new Date(),
      };

      // Add user message and set typing
      setState((prev) => ({
        ...prev,
        messages: [...prev.messages, userMsg],
        isTyping: true,
      }));

      try {
        // Check for URLs in the message
        const urls = extractUrls(content);
        let urlAnalysis = "";

        if (urls.length > 0) {
          // Analyze the first URL found
          try {
            const urlResult = await analyzeUrlMutation.mutateAsync({
              url: urls[0],
            });

            if (urlResult.success && urlResult.content) {
              const c = urlResult.content;
              urlAnalysis = `\n\n[URL ANALYSIS for ${c.url}]\nTitle: ${c.title || "N/A"}\nDescription: ${c.description || "N/A"}\nKeywords: ${c.keywords || "N/A"}\nSocial Links: YouTube: ${c.socialLinks?.youtube || "N/A"}, Twitter: ${c.socialLinks?.twitter || "N/A"}, Instagram: ${c.socialLinks?.instagram || "N/A"}, TikTok: ${c.socialLinks?.tiktok || "N/A"}\nSubscribers/Followers: ${c.subscriberCount || "N/A"}\nPage Content Summary: ${c.textContent?.slice(0, 2000) || "N/A"}\n[END URL ANALYSIS]`;
            } else {
              urlAnalysis = `\n\n[URL ANALYSIS FAILED for ${urls[0]}: ${urlResult.error || "Could not fetch the page"}. Please use your knowledge about this creator/website to provide insights.]`;
            }
          } catch {
            urlAnalysis = `\n\n[URL ANALYSIS FAILED for ${urls[0]}: Network error. Please use your knowledge about this creator/website to provide insights.]`;
          }
        }

        // Build conversation history for the LLM
        // Include all previous messages (excluding the welcome system message)
        const conversationHistory = state.messages
          .filter((m) => m.id !== "welcome-1")
          .map((m) => ({
            role: m.role as "user" | "assistant",
            content: m.content,
          }));

        // Add the current user message with URL analysis appended if applicable
        const userContent = urlAnalysis
          ? `${content}${urlAnalysis}`
          : content;

        conversationHistory.push({
          role: "user",
          content: userContent,
        });

        // Call the LLM
        const result = await chatMutation.mutateAsync({
          messages: conversationHistory,
        });

        // Update game config if the LLM returned config updates
        const newConfig = { ...state.gameConfig };
        if (result.gameConfig) {
          const gc = result.gameConfig as Record<string, unknown>;
          if (gc.title && typeof gc.title === "string") newConfig.title = gc.title;
          if (gc.gameType && typeof gc.gameType === "string") newConfig.gameType = gc.gameType;
          if (gc.description && typeof gc.description === "string") newConfig.description = gc.description;
          // Clamp LLM-provided economics to platform rules. The model may
          // echo cent figures as dollars or invent out-of-range cuts.
          if (typeof gc.entryFee === "number" && gc.entryFee === 0) {
            newConfig.entryFee = 0; // free event, no prize pool
          } else if (gc.entryFee && typeof gc.entryFee === "number") {
            const fee = Math.min(Math.max(gc.entryFee, 0), 10000); // ticket price $1–$10,000
            newConfig.entryFee = fee > 0 ? Math.max(fee, 1) : 0; // non-zero floors at $1 (server rule)
          }
          if (gc.maxPlayers && typeof gc.maxPlayers === "number") {
            newConfig.maxPlayers = Math.min(Math.max(Math.round(gc.maxPlayers), 2), 10000);
          }
          if (gc.duration && typeof gc.duration === "string") newConfig.duration = gc.duration;
          if (gc.hostCut && typeof gc.hostCut === "number") newConfig.hostCut = Math.min(Math.round(gc.hostCut), 15);
          if (gc.promoterCut && typeof gc.promoterCut === "number") newConfig.promoterCut = Math.min(Math.round(gc.promoterCut), 15);
          if (gc.winCondition && typeof gc.winCondition === "string") {
            // Reject question-like values — these are likely user questions misinterpreted by the LLM
            const wc = gc.winCondition.trim();
            if (!wc.endsWith("?") && !wc.toLowerCase().startsWith("what") && !wc.toLowerCase().startsWith("how") && !wc.toLowerCase().startsWith("can") && !wc.toLowerCase().startsWith("should") && wc.length > 5) {
              newConfig.winCondition = wc;
            }
          }
          if (gc.rules && Array.isArray(gc.rules)) {
            // Filter out question-like rules
            const validRules = (gc.rules as string[]).filter((r) => {
              const trimmed = typeof r === "string" ? r.trim() : "";
              return trimmed.length > 3 && !trimmed.endsWith("?") && !trimmed.toLowerCase().startsWith("what") && !trimmed.toLowerCase().startsWith("how about");
            });
            if (validRules.length > 0) newConfig.rules = validRules;
          }
          if (gc.targetAudience && typeof gc.targetAudience === "string") newConfig.targetAudience = gc.targetAudience;
          if (typeof gc.viralScore === "number") newConfig.viralScore = gc.viralScore;
          if (typeof gc.complianceScore === "number") newConfig.complianceScore = gc.complianceScore;
          if (gc.socialCaption && typeof gc.socialCaption === "string") newConfig.socialCaption = gc.socialCaption;
          if (gc.emailPitch && typeof gc.emailPitch === "string") newConfig.emailPitch = gc.emailPitch;
          if (gc.marketingHook && typeof gc.marketingHook === "string") newConfig.marketingHook = gc.marketingHook;
          if (gc.influencerNiche && typeof gc.influencerNiche === "string") newConfig.influencerNiche = gc.influencerNiche;
          if (gc.submissionType && typeof gc.submissionType === "string") newConfig.submissionType = gc.submissionType as GameConfig["submissionType"];
          if (gc.votingMethod && typeof gc.votingMethod === "string") {
            const vm = gc.votingMethod as GameConfig["votingMethod"];
            // Beta: no vote- or likes-decided winners.
            newConfig.votingMethod = betaActive && (vm === "community_vote" || vm === "hybrid") ? "auto_score" : vm;
          }
          if (gc.influencerChannelUrl && typeof gc.influencerChannelUrl === "string") newConfig.influencerChannelUrl = gc.influencerChannelUrl;
          if (gc.playerShareTemplate && typeof gc.playerShareTemplate === "string") newConfig.playerShareTemplate = gc.playerShareTemplate;
          if (gc.influencerFeatureTemplate && typeof gc.influencerFeatureTemplate === "string") newConfig.influencerFeatureTemplate = gc.influencerFeatureTemplate;
        }

        // Infer the current phase from the response
        const inferredPhase = inferPhase(result.content, state.phase, newConfig);
        const newPhase =
          phaseOrder.indexOf(inferredPhase) >= phaseOrder.indexOf(state.phase)
            ? inferredPhase
            : state.phase;

        const assistantMsg: ChatMessage = {
          id: `assistant-${Date.now()}`,
          role: "assistant",
          content: result.content,
          timestamp: new Date(),
          phase: newPhase,
        };

        setState((prev) => ({
          ...prev,
          messages: [...prev.messages, assistantMsg],
          gameConfig: newConfig,
          phase: newPhase,
          isTyping: false,
          phaseProgress: calculateProgress(newPhase),
        }));
      } catch (error) {
        // On error, show an error message
        const errorMsg: ChatMessage = {
          id: `error-${Date.now()}`,
          role: "assistant",
          content:
            "I encountered an issue processing your request. Please try again or rephrase your message.",
          timestamp: new Date(),
        };

        setState((prev) => ({
          ...prev,
          messages: [...prev.messages, errorMsg],
          isTyping: false,
        }));
      }
    },
    [state.messages, state.gameConfig, state.phase, chatMutation, analyzeUrlMutation, betaActive]
  );

  const setPhase = useCallback((phase: MissionPhase) => {
    setState((prev) => ({ ...prev, phase }));
  }, []);

  const updateGameConfig = useCallback((updates: Partial<GameConfig>) => {
    setState((prev) => ({
      ...prev,
      gameConfig: { ...prev.gameConfig, ...updates },
    }));
  }, []);

  const resetCreator = useCallback(() => {
    setState({
      phase: "welcome",
      messages: [
        {
          id: "welcome-reset",
          role: "assistant",
          content: WELCOME_MESSAGE,
          timestamp: new Date(),
          phase: "welcome",
        },
      ],
      gameConfig: defaultGameConfig,
      isTyping: false,
      phaseProgress: {
        welcome: 100,
        audience: 0,
        design: 0,
        rules: 0,
        marketing: 0,
        launch: 0,
      },
    });
  }, []);

  return (
    <GameCreatorContext.Provider
      value={{
        ...state,
        sendMessage,
        setPhase,
        updateGameConfig,
        resetCreator,
      }}
    >
      {children}
    </GameCreatorContext.Provider>
  );
}

export function useGameCreator() {
  const context = useContext(GameCreatorContext);
  if (!context) throw new Error("useGameCreator must be used within GameCreatorProvider");
  return context;
}
